import React, { useEffect, useState } from 'react'
import styles from 'styles/components/CancelSubscriptionConfirmationCard.module.sass'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'hooks/AuthContext'
import ReusableIcon from 'components/common/ReusableIcon'
import PrimaryButton from 'components/common/PrimaryButton'
import SecondaryButton from 'components/common/SecondaryButton'
import { useToast } from 'provider/ToastProvider'
import { cancelSubscription } from 'api/SubscriptionApiService'
import ZeroneLoader from 'components/common/ZeroneLoader'
import { logError } from 'services/logService/errorLogger'
import { Option } from 'interface/Option'
import SearchDropdown from 'components/common/SearchDropdown'
import InputField from 'components/common/InputField'

interface CancelSubscriptionConfirmationCardProps {
    onClose: () => void
}


const cancellationReasons: Option[] = [
    { id: 'too_expensive', name: 'The subscription cost is higher than I\'m willing to pay.' },
    { id: 'not_using', name: 'I don\'t use this enough to justify the subscription.' },
    { id: 'switching', name: 'I found another service that suits my needs better.' },
    { id: 'technical_issues', name: 'I\'m experiencing technical issues with the service.' },
    { id: 'billing_errors', name: 'I\'ve encountered billing errors or issues.' },
    { id: 'temporary_needs', name: 'I only needed the subscription for a short period of time.' },
    { id: 'customer_support', name: 'I had a poor experience with customer support.' },
    { id: 'company_budget', name: 'My organization\'s budget no longer allows for this subscription.' },
    { id: 'company_policy', name: 'My organization no longer requires this subscription.' },
    { id: 'privacy_concerns', name: 'I have privacy concerns about my data.' },
    { id: 'other', name: 'Other' }
]

/**
 * CancelSubscriptionConfirmationCard component displays a confirmation card
 * for canceling a subscription.
 *
 * This card is displayed when a user attempts to cancel their subscription.
 *
 * @component
 * @param {CancelSubscriptionConfirmationCardProps} props - The properties for the CancelSubscriptionConfirmationCard component.
 * @param {() => void} props.onClose - The callback function to close the card.
 *
 * @example
 * // Example usage:
 * <CancelSubscriptionConfirmationCard
 *   onClose={() => console.log('Close button clicked')}
 * />
 *
 * @returns {JSX.Element} The rendered CancelSubscriptionConfirmationCard component.
 */
const CancelSubscriptionConfirmationCard: React.FC<CancelSubscriptionConfirmationCardProps> = ({ onClose }) => {
    const { addToast } = useToast()
    const { user, userSubscription } = useAuth()
    const [reason, setReason] = useState<string>('')
    const [other, setOther] = useState<string>('')
    const [planName, setPlanName] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [message, setMessage] = useState<string>('')
    const navigate = useNavigate()

    useEffect(() => {
        if (!user || !userSubscription || userSubscription.name === 'free') {
            navigate('/login')
        } else {
            const subscriptionPlan = userSubscription.name.charAt(0).toUpperCase() + userSubscription.name.slice(1)
            setPlanName(subscriptionPlan)
        }
    }, [user, userSubscription, navigate])

    const downgradePlanToFree = async () => {
        if (!reason?.trim() || (reason === 'other' && !other?.trim())) {
            addToast({
                type: 'error',
                message: reason === 'other' ? 'Please provide a reason for canceling your subscription.' : 'Please select a reason for canceling your subscription.'
            })
            return
        }
        setLoading(true)
        setMessage('Canceling your subscription...')

        try {
            const result = await cancelSubscription({ 
                tag: 'CancelSubscriptionConfirmation',
                data: {
                    reason: reason === 'other' ? `other: ${other}` : reason
                }
            })

            if (!result.success || 'error' in result) {
                addToast({
                    type: 'error',
                    message: result.error || 'An error occurred while canceling your subscription. Please try again later.'
                })
            } else {
                addToast({
                    type: 'success',
                    message: 'Your subscription has been canceled successfully.'
                })
                /**
                 * Trigger auth change event to update user subscription.
                 * This is a workaround to update the user subscription in the auth context.
                 * This is because the user subscription is not updated in the auth context after payment is successful.
                */
                window.dispatchEvent(new Event('authChange'))
            }
        } catch (error) {
            logError('Error canceling subscription.', { error }, 'CancelSubscriptionConfirmationCard')
            addToast({
                type: 'error',
                message: 'An error occurred while canceling your subscription. Please try again later.'
            })
        } finally {
            setMessage('')
            setLoading(false)
            onClose()
        }
    }

    const handleCancelSubscription = () => {
        downgradePlanToFree()
    }

    const handleKeepSubscription = () => {
        addToast({
            type: 'success',
            message: 'Thank you for staying with us!'
        })
        onClose()
    }

    const handleReasonSelect = (selectedReason: string) => {
        setReason(selectedReason)
    }

    return (
        <div className={styles['cancel-subscription-confirmation-card']}>
            <button className={styles['close-button']} onClick={onClose}>
                <ReusableIcon
                    icon={'qlementine-icons:close-16'}
                    className={styles['close-icon'] || 'close-icon'}
                    color={'#707070'}
                />
            </button>
            <div className={styles['main-content']}>
                <div className={styles['right-wrapper']}>
                    <h3 className={styles['title']}>Are you sure you want to cancel your subscription?</h3>
                    <p className={styles['description']}>
                        Canceling your subscription will remove your access to all {planName} plan features and reports. You will still have access to your account until the end of your billing cycle. Your account will be downgraded to the free plan after that.
                    </p>
                </div>
            </div>
            <div className={styles['reason-section']}>       
                <SearchDropdown
                    options={cancellationReasons}
                    buttonName=''
                    placeholder='Select a reason for cancellation'
                    buttonColor={'transparent'}
                    onSelect={handleReasonSelect}
                />
                {reason === 'other' && (
                    <InputField
                        label={'Please specify your reason'}
                        labelColor='#e3e3e3'
                        type={'text'}
                        value={other}
                        onChange={setOther}
                        required={true}
                    />
                )}
            </div>
            <div className={styles['button-group']}>
                <PrimaryButton
                    text="Yes, Cancel Subscription"
                    primaryColor="#014365"
                    textColor="#e3e3e3"
                    hoverColor="#FF5522"
                    hoverTextColor="#FFFFFF"
                    doSomething={() => handleCancelSubscription()}
                />
                <SecondaryButton
                    text="No, Keep Subscription"
                    primaryColor="transparent"
                    textColor="#FF5522"
                    hoverColor="#FF5522"
                    hoverTextColor="#FFFFFF"
                    doSomething={() => handleKeepSubscription()}
                />
            </div>
            
            {loading && (
                <div className={styles['loader']}>
                    <ZeroneLoader text={message} />
                </div>
            )}
        </div>
    )
}

export default CancelSubscriptionConfirmationCard